/**
 * colorUtils — the canonical color-math module for the design system.
 *
 * Ported verbatim from the unified app (`src/lib/colorUtils.ts`), which was the
 * only one of three donor copies with real input guards. Three repos each
 * hand-rolled this conversion math (and one inline copy in fe-react silently
 * produced `NaN` on a malformed hex); this is the single robust version every
 * consumer and the `<ColorPicker>` now share.
 *
 * Scales: hue `h` is 0-360, saturation `s` and value `v` are 0-100, RGB
 * channels are 0-255. Hex is always lowercase, 6-digit, `#`-prefixed on output.
 *
 *   import { hexToRgb, rgbToHex, hexToHSV, hsvToHex } from "@aisquare/ui";
 *
 *   hexToRgb("#0a2540");   // { r: 10, g: 37, b: 64 }
 *   hexToRgb("nope");      // null  ← callers MUST handle null
 *   hexToHSV("nope");      // { h: 0, s: 0, v: 0 }  ← never NaN
 *   rgbToHex(10, 37, 64);  // "#0a2540"
 *   hsvToHex(210, 84, 25); // "#0a2540"
 */

/**
 * Parse a hex color to RGB channels (0-255), or `null` on a malformed string.
 *
 * The leading `#` is optional (`#?`). Only an exact 6-hex-digit match parses;
 * anything else (short hex, garbage, named color) returns `null`. Callers must
 * keep handling the `null` case — it is the signal that the input is not a
 * usable color, and skipping it is how the fe-react inline copy produced `NaN`.
 */
export const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) }
    : null;
};

/**
 * Pack RGB channels into a 6-digit lowercase `#rrggbb` hex. Each channel is
 * clamped to 0-255 and rounded first, so out-of-range / fractional input
 * (e.g. from a float color computation) can never produce a short or invalid
 * string.
 */
export const rgbToHex = (r: number, g: number, b: number): string => {
  const rc = Math.max(0, Math.min(255, Math.round(r)));
  const gc = Math.max(0, Math.min(255, Math.round(g)));
  const bc = Math.max(0, Math.min(255, Math.round(b)));
  return `#${((1 << 24) | (rc << 16) | (gc << 8) | bc).toString(16).slice(1)}`;
};

/**
 * Convert a hex color to HSV (`h` 0-360, `s`/`v` 0-100, all rounded to
 * integers). Returns `{ h: 0, s: 0, v: 0 }` (never `NaN`) when the hex is
 * malformed, via `hexToRgb`'s `null` guard. Round-trips with {@link hsvToHex}.
 */
export const hexToHSV = (hex: string): { h: number; s: number; v: number } => {
  const rgb = hexToRgb(hex);
  if (!rgb) return { h: 0, s: 0, v: 0 };
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const diff = max - min;
  let h = 0;
  if (diff !== 0) {
    if (max === r) h = 60 * (((g - b) / diff) % 6);
    else if (max === g) h = 60 * ((b - r) / diff + 2);
    else h = 60 * ((r - g) / diff + 4);
  }
  if (h < 0) h += 360;
  const s = max === 0 ? 0 : (diff / max) * 100;
  const v = max * 100;
  return { h: Math.round(h), s: Math.round(s), v: Math.round(v) };
};

/**
 * Convert HSV (`h` 0-360, `s`/`v` 0-100) to a 6-digit lowercase hex. Uses the
 * chroma form (`c = v*s`, `x = c*(1 - |(h/60)%2 - 1|)`, `m = v - c`) routed by
 * 60-degree sextant. Round-trips with {@link hexToHSV}.
 */
export const hsvToHex = (h: number, s: number, v: number): string => {
  s = s / 100;
  v = v / 100;
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h >= 0 && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (h >= 60 && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (h >= 180 && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (h >= 240 && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else {
    r = c;
    g = 0;
    b = x;
  }
  return rgbToHex((r + m) * 255, (g + m) * 255, (b + m) * 255);
};

/**
 * Normalize a hex string to canonical 6-digit lowercase `#rrggbb`, or `null`
 * if it is not a valid hex. Adds the missing `#`, lowercases, and (for valid
 * input) is idempotent. Useful for comparing a candidate color against a preset
 * list where the two may differ only in case or `#` prefix.
 *
 *   normalizeHex("0A2540"); // "#0a2540"
 *   normalizeHex("#0A2540"); // "#0a2540"
 *   normalizeHex("xyz");    // null
 */
export const normalizeHex = (hex: string): string | null => {
  const rgb = hexToRgb(hex);
  return rgb ? rgbToHex(rgb.r, rgb.g, rgb.b) : null;
};

/**
 * Pick the more readable foreground (`"#000000"` or `"#ffffff"`) to lay over a
 * given background hex, using the WCAG relative-luminance threshold (0.179). On
 * a malformed hex it falls back to black. Lets a swatch render an in-contrast
 * check / label without the consumer re-deriving luminance.
 *
 *   readableTextColor("#daa520"); // "#000000"  (light gold → black text)
 *   readableTextColor("#0a2540"); // "#ffffff"  (navy → white text)
 */
export const readableTextColor = (hex: string): "#000000" | "#ffffff" => {
  const rgb = hexToRgb(hex);
  if (!rgb) return "#000000";
  // sRGB → linear, then relative luminance (WCAG 2.x).
  const channel = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const luminance = 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
  return luminance > 0.179 ? "#000000" : "#ffffff";
};
